import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Utensils, Heart, Power, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { PetAvatar } from "@/components/PetAvatar";
import { PetTalk } from "@/components/ai/PetTalk";
import { PetShop } from "@/components/PetShop";
import { PetVisits } from "@/components/PetVisits";

const MOOD = {
  gluecklich: ["Glücklich", "text-rose-600", ""], hungrig: ["Hungrig", "text-amber-600", ""], traurig: ["Traurig", "text-sky-600", "grayscale-[30%]"],
  krank: ["Krank – bitte füttern!", "text-red-600", "grayscale-[60%] opacity-80"], tot: ["Gestorben", "text-slate-500", "grayscale opacity-50"],
};
const SIZE = ["w-40 h-40", "w-48 h-48", "w-56 h-56", "w-64 h-64", "w-72 h-72"];

function Adopt({ kinds, onDone, memorial }) {
  const [kind, setKind] = useState("hund");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post("/pet/adopt", { kind, name }); toast.success(`Willkommen, ${name}!`); onDone(data); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-3xl border border-pink-100 p-5 sm:p-6 space-y-4" data-testid="pet-adopt">
      <h2 className="font-display text-xl font-extrabold">{memorial ? "Ein neues Haustier adoptieren" : "Such dir dein Haustier aus"}</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Object.entries(kinds).map(([k, l]) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`pet-kind-${k}`} aria-pressed={kind === k}
            className={`rounded-2xl p-2 border-2 transition-[transform,border-color] active:scale-95 ${kind === k ? "border-rose-400 scale-[1.03]" : "border-transparent"}`}>
            <img src={`/pets/${k}.webp`} alt={l} className="w-full aspect-square rounded-xl object-cover" />
            <p className="font-bold text-sm mt-1.5">{l}</p>
          </button>
        ))}
      </div>
      <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={20} placeholder="Name deines Haustiers" data-testid="pet-name-input" className="h-12 rounded-full border-pink-100" />
      <button onClick={go} disabled={!name.trim() || busy} data-testid="pet-adopt-btn" className="w-full h-12 rounded-full pink-gradient text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />} Adoptieren
      </button>
    </div>
  );
}

function PetCard({ d, setD }) {
  const p = d.pet;
  const [busy, setBusy] = useState(false);
  const { refreshUser } = useAuth();
  const [m, color, filter] = MOOD[p.mood];
  const pct = p.next_xp ? Math.min(100, Math.round((p.xp / p.next_xp) * 100)) : 100;
  const feed = async () => {
    setBusy(true);
    try { const { data } = await api.post("/pet/feed"); setD(data); toast.success(`${p.name} ist satt und glücklich (+${d.feed_points} Punkte)`); refreshUser?.(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-3xl border border-pink-100 p-5 sm:p-8 text-center space-y-4" data-testid="pet-card">
      <div className="relative mx-auto w-fit">
        <PetAvatar kind={p.kind} name={p.name} equipped={p.equipped} testId="pet-image" className={`${SIZE[p.stage_index]} mx-auto transition-[width,height] duration-700 ${p.mood === "gluecklich" ? "pet-bounce" : ""}`} imgClass={`shadow-xl shadow-rose-100 ${filter}`} />
        {p.fed_today && <span className="absolute -top-1 -right-1 w-10 h-10 rounded-full bg-white shadow flex items-center justify-center"><Heart className="w-5 h-5 text-rose-500 fill-rose-500" /></span>}
      </div>
      <div>
        <h2 className="font-display text-3xl font-extrabold" data-testid="pet-name">{p.name}</h2>
        <p className="text-sm text-slate-500"><span data-testid="pet-stage">{p.stage}</span> · <span className={`font-bold ${color}`} data-testid="pet-mood">{m}</span></p>
      </div>
      {p.mood !== "tot" && <PetTalk mood={p.mood} />}
      <div className="max-w-xs mx-auto">
        <div className="h-2.5 rounded-full bg-rose-50 overflow-hidden"><div className="h-full pink-gradient" style={{ width: `${pct}%` }} /></div>
        <p className="text-xs text-slate-500 mt-1" data-testid="pet-xp">{p.next_xp ? `${p.xp} / ${p.next_xp} Wachstum bis zur nächsten Stufe` : "Höchste Stufe erreicht!"}</p>
      </div>
      {p.mood !== "tot" && (
        <button onClick={feed} disabled={busy || p.fed_today} data-testid="pet-feed-btn" className="h-12 px-8 rounded-full pink-gradient text-white font-bold inline-flex items-center gap-2 disabled:opacity-50 active:scale-95 transition-transform">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Utensils className="w-4 h-4" />} {p.fed_today ? "Heute schon gefüttert" : "Füttern"}
        </button>
      )}
      <p className="text-xs text-slate-400 max-w-sm mx-auto">Füttere {p.name} jeden Tag (kostenlos, +{d.feed_points} Punkte). Mit deinen gesammelten Punkten wächst es. Ohne Futter wird es hungrig, traurig, nach 4 Tagen krank – und nach 7 Tagen stirbt es.</p>
    </div>
  );
}

export default function Pet() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/pet").then(({ data }) => setD(data)); }, []);
  const toggle = async (enabled) => {
    try { const { data } = await api.post("/pet/toggle", { enabled }); setD(data); toast.success(enabled ? "Haustier wieder eingeschaltet" : "Haustier ausgeschaltet – es wartet geduldig auf dich"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  if (!d) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>;
  const p = d.pet;
  const off = p && p.enabled === false;
  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="pet-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><p className="text-xs tracking-wider uppercase font-semibold text-rose-600">Haustier-Begleiter</p><h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-2"><Sparkles className="w-7 h-7 text-rose-500" /> Mein Haustier</h1></div>
        {p && <label className="flex items-center gap-2 text-sm font-semibold text-slate-600"><Power className="w-4 h-4" /> Haustier aktiv <Switch checked={!off} onCheckedChange={toggle} data-testid="pet-toggle" /></label>}
      </div>
      {off ? <div className="bg-white rounded-3xl border border-pink-100 p-6 text-center text-slate-500" data-testid="pet-off">Dein Haustier ist ausgeschaltet. Solange es aus ist, bekommt es keinen Hunger und du bekommst keine Erinnerungen.</div>
        : !p ? <Adopt kinds={d.kinds} onDone={setD} />
          : <>
            <PetCard d={d} setD={setD} />
            <Link to="/spielplatz" data-testid="pet-to-playground" className="block text-center text-sm font-bold text-rose-600">Zum Haustier-Spielplatz – zeig dein Outfit →</Link>
            {p.mood !== "tot" && <PetVisits myPet={p.name} />}
            {p.mood !== "tot" && <PetShop shop={d.shop} equipped={p.equipped} onEquipped={(eq) => setD((x) => ({ ...x, pet: { ...x.pet, equipped: eq } }))} />}
            {p.mood === "tot" && <Adopt kinds={d.kinds} onDone={setD} memorial />}
          </>}
    </div>
  );
}
